import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { X, Send, Trash2, Bot } from 'lucide-react';
import { toggleChatbot, closeChatbot, sendMessageRequest, clearHistory } from '../../store/chatbot/_actions';

// Renders Gemini markdown: headers, bold, italic, inline code, code blocks, lists
const MarkdownMessage = ({ text, isStreaming }) => {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  const parseInline = (str) => {
    // Split on bold, italic, inline-code tokens while preserving them
    const parts = str.split(/(\*\*[\s\S]*?\*\*|__[\s\S]*?__|`[^`]+`|\*[^*]+\*|_[^_]+_)/g);
    return parts.map((part, idx) => {
      if (/^\*\*(.+)\*\*$/.test(part) || /^__(.+)__$/.test(part)) {
        const inner = part.slice(2, -2);
        return <strong key={idx} className="font-semibold">{inner}</strong>;
      }
      if (/^`([^`]+)`$/.test(part)) {
        return <code key={idx} className="bg-gray-100 text-pink-600 px-1 py-0.5 rounded text-[12px] font-mono">{part.slice(1, -1)}</code>;
      }
      if (/^\*([^*]+)\*$/.test(part) || /^_([^_]+)_$/.test(part)) {
        return <em key={idx}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.trimStart().startsWith('```')) {
      const lang = line.replace(/^```/, '').trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={i} className="bg-gray-900 text-green-300 rounded-lg p-3 my-2 overflow-x-auto text-[11px] font-mono leading-relaxed">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      i++;
      continue;
    }

    // Headings
    const h3 = line.match(/^###\s+(.*)/);
    const h2 = line.match(/^##\s+(.*)/);
    const h1 = line.match(/^#\s+(.*)/);
    if (h1) { elements.push(<h1 key={i} className="text-base font-bold mt-2 mb-1">{parseInline(h1[1])}</h1>); i++; continue; }
    if (h2) { elements.push(<h2 key={i} className="text-sm font-bold mt-2 mb-1">{parseInline(h2[1])}</h2>); i++; continue; }
    if (h3) { elements.push(<h3 key={i} className="text-sm font-semibold mt-1.5 mb-0.5">{parseInline(h3[1])}</h3>); i++; continue; }

    // Unordered list item
    const li = line.match(/^[-*+]\s+(.*)/);
    if (li) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*+]\s+(.*)/)) {
        const m = lines[i].match(/^[-*+]\s+(.*)/);
        items.push(<li key={i} className="ml-4 list-disc">{parseInline(m[1])}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="my-1 space-y-0.5">{items}</ul>);
      continue;
    }

    // Ordered list item
    const oli = line.match(/^\d+\.\s+(.*)/);
    if (oli) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s+(.*)/)) {
        const m = lines[i].match(/^\d+\.\s+(.*)/);
        items.push(<li key={i} className="ml-4 list-decimal">{parseInline(m[1])}</li>);
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="my-1 space-y-0.5">{items}</ol>);
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={i} className="border-gray-200 my-2" />);
      i++; continue;
    }

    // Blank line → spacing
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-1.5" />);
      i++; continue;
    }

    // Regular paragraph
    elements.push(
      <p key={i} className="leading-relaxed">{parseInline(line)}</p>
    );
    i++;
  }

  return (
    <span className="text-sm">
      {elements}
      {isStreaming && (
        <span className="inline-block w-0.5 h-3.5 bg-gray-500 ml-0.5 animate-pulse align-middle" />
      )}
    </span>
  );
};

const SIZE_PRESETS = {
  sm:  { width: 300, height: 380 },
  md:  { width: 360, height: 500 },
  lg:  { width: 520, height: 680 },
};

const MIN_W = 260;
const MAX_W = 760;
const MIN_H = 300;
const MAX_H = 860;

const ChatbotWidget = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { isOpen, messages, isTyping, error, rateLimit, rateLimitExceeded, chatbotDisabled } = useSelector((state) => state.chatbot);
  const { user } = useSelector((state) => state.auth);

  const [input, setInput] = useState('');
  const [size, setSize] = useState(SIZE_PRESETS.md);
  const [activePreset, setActivePreset] = useState('md');

  const role = user?.role?.toUpperCase();
  const isLeftSide = role === 'ADMIN' || role === 'COLLEGE';

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const dragRef = useRef(null); // tracks { startX, startY, startW, startH }

  // Auto-scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  // ── Resize drag handlers ──────────────────────────────────────────────────
  const onMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { startX, startY, startW, startH, leftSide } = dragRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    // Left-anchored: drag right-handle → width grows with dx; Right-anchored: drag left-handle → width grows as dx decreases
    const newW = leftSide
      ? Math.min(MAX_W, Math.max(MIN_W, startW + dx))
      : Math.min(MAX_W, Math.max(MIN_W, startW - dx));
    const newH = Math.min(MAX_H, Math.max(MIN_H, startH - dy));
    setSize({ width: newW, height: newH });
    setActivePreset(null);
  }, []);

  const onMouseUp = useCallback(() => {
    dragRef.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [onMouseMove]);

  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: size.width,
      startH: size.height,
      leftSide: isLeftSide,
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = isLeftSide ? 'ne-resize' : 'nw-resize';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [size, isLeftSide, onMouseMove, onMouseUp]);

  const applyPreset = (key) => {
    setSize(SIZE_PRESETS[key]);
    setActivePreset(key);
  };

  // ── Message send ──────────────────────────────────────────────────────────
  const handleSend = () => {
    const text = input.trim();
    if (!text || isTyping || rateLimitExceeded || chatbotDisabled) return;
    dispatch(sendMessageRequest(text));
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Hide on student exam pages
  if (role === 'STUDENT' && router.pathname.startsWith('/student/exam')) {
    return null;
  }

  return (
    <>
      {/* Chat Panel */}
      <div
        className={`fixed z-[60] bottom-24 ${isLeftSide ? 'left-4' : 'right-4'} transition-opacity duration-300 ease-in-out
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none scale-95'}
        `}
        style={{
          width: size.width,
          transformOrigin: isLeftSide ? 'bottom left' : 'bottom right',
          transition: isOpen ? 'opacity 0.3s ease, transform 0.3s ease' : 'opacity 0.3s ease, transform 0.3s ease',
          transform: isOpen ? 'scale(1)' : 'scale(0.95)',
        }}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden relative"
          style={{ height: size.height }}
        >
          {/* ── Resize handle (corner opposite the anchor edge) ── */}
          <div
            onMouseDown={onResizeMouseDown}
            className={`absolute top-0 ${isLeftSide ? 'right-0' : 'left-0'} w-5 h-5 z-10 ${isLeftSide ? 'cursor-ne-resize' : 'cursor-nw-resize'} group`}
            title="Drag to resize"
          >
            {/* Visual grip dots */}
            <svg
              width="14" height="14"
              viewBox="0 0 14 14"
              className={`absolute top-1 ${isLeftSide ? 'right-1' : 'left-1'} opacity-0 group-hover:opacity-60 transition-opacity`}
            >
              <circle cx="3" cy="3" r="1.2" fill="white" />
              <circle cx="7" cy="3" r="1.2" fill="white" />
              <circle cx="3" cy="7" r="1.2" fill="white" />
              <circle cx="7" cy="7" r="1.2" fill="white" />
              <circle cx="3" cy="11" r="1.2" fill="white" />
            </svg>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <img
                src="/Gyan_Chand.png"
                alt="Gyan Chand"
                className="w-8 h-8 rounded-full object-cover border-2 border-white/30"
              />
              <div>
                <p className="text-sm font-semibold leading-tight">Gyan Chand</p>
                <p className="text-xs text-blue-100 leading-tight">
                  {isTyping ? 'Typing...' : 'Online'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {/* Size presets */}
              <div className="flex items-center gap-0.5 mr-1 bg-white/10 rounded-lg p-0.5">
                <button
                  onClick={() => applyPreset('sm')}
                  title="Small"
                  className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors
                    ${activePreset === 'sm' ? 'bg-white text-blue-600' : 'text-white/80 hover:text-white hover:bg-white/20'}`}
                >S</button>
                <button
                  onClick={() => applyPreset('md')}
                  title="Medium"
                  className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors
                    ${activePreset === 'md' ? 'bg-white text-blue-600' : 'text-white/80 hover:text-white hover:bg-white/20'}`}
                >M</button>
                <button
                  onClick={() => applyPreset('lg')}
                  title="Large"
                  className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors
                    ${activePreset === 'lg' ? 'bg-white text-blue-600' : 'text-white/80 hover:text-white hover:bg-white/20'}`}
                >L</button>
              </div>

              {messages.length > 0 && (
                <button
                  onClick={() => dispatch(clearHistory())}
                  className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                  title="Clear chat"
                >
                  <Trash2 size={14} />
                </button>
              )}
              <button
                onClick={() => dispatch(closeChatbot())}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <img
                  src="/Gyan_Chand.png"
                  alt="Gyan Chand"
                  className="w-14 h-14 rounded-full object-cover mb-3 border-2 border-blue-100 shadow-sm"
                />
                <p className="text-sm font-semibold text-gray-800">Gyan Chand</p>
                <p className="text-sm font-medium text-gray-600 mt-0.5">
                  Hi{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! 👋
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Ask me anything about your courses, exams, or learning journey.
                </p>
                <p className="text-[10px] text-gray-400 mt-3">
                  Drag the corner handle to resize
                </p>
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'bot' && (
                  <img
                    src="/Gyan_Chand.png"
                    alt="Gyan Chand"
                    className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-1 mr-2"
                  />
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-2 leading-relaxed
                    ${msg.role === 'user'
                      ? 'bg-blue-600 text-white text-sm rounded-br-sm'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm'
                    }`}
                >
                  {msg.role === 'user' ? (
                    <>
                      {msg.text}
                      {msg.isStreaming && (
                        <span className="inline-block w-0.5 h-3.5 bg-white ml-0.5 animate-pulse align-middle" />
                      )}
                    </>
                  ) : (
                    <MarkdownMessage text={msg.text} isStreaming={msg.isStreaming} />
                  )}
                </div>
              </div>
            ))}

            {/* Typing dots */}
            {isTyping && messages.length > 0 && messages[messages.length - 1].role === 'bot' && messages[messages.length - 1].text === '' && (
              <div className="flex justify-start">
                <img
                  src="/Gyan_Chand.png"
                  alt="Gyan Chand"
                  className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-1 mr-2"
                />
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm shadow-sm px-4 py-3">
                  <div className="flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-500 text-center px-2">{error}</p>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Rate limit / disabled banners + Input */}
          <div className="border-t border-gray-100 bg-white flex-shrink-0">
            {/* Usage counter */}
            {rateLimit && !rateLimitExceeded && !chatbotDisabled && (
              <div className="px-3 pt-2 flex justify-end">
                <span className="text-[10px] text-gray-400">
                  {rateLimit.remaining} of {rateLimit.daily_limit} chats remaining today
                </span>
              </div>
            )}

            {/* Chatbot disabled banner */}
            {chatbotDisabled && (
              <div className="mx-3 my-2 rounded-xl bg-gray-100 border border-gray-200 px-4 py-3 text-center">
                <p className="text-sm font-medium text-gray-600">Chatbot is currently unavailable</p>
                <p className="text-xs text-gray-400 mt-0.5">Please check back later.</p>
              </div>
            )}

            {/* Daily limit reached banner */}
            {rateLimitExceeded && !chatbotDisabled && (
              <div className="mx-3 my-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-center">
                <p className="text-sm font-medium text-amber-700">Daily chat limit reached</p>
                <p className="text-xs text-amber-500 mt-0.5">Resets at midnight · {rateLimit?.daily_limit} chats/day</p>
              </div>
            )}

            {/* Input row — hidden when disabled/exceeded */}
            {!chatbotDisabled && !rateLimitExceeded && (
              <div className="flex items-end gap-2 p-3">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  disabled={isTyping}
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 max-h-24 overflow-y-auto"
                  style={{ lineHeight: '1.4' }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping}
                  className="flex-shrink-0 w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Send size={15} className="text-white" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Trigger Button */}
      <div className={`fixed bottom-10 ${isLeftSide ? 'left-36' : 'right-16'} z-[60]`}>
        {!isOpen && (
          <span className="absolute inset-0 rounded-full bg-blue-400 opacity-60 animate-ping" />
        )}
        <button
          onClick={() => dispatch(toggleChatbot())}
          className={`relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 overflow-hidden
            ${isOpen ? 'bg-gray-700 hover:bg-gray-800' : 'bg-white'}`}
          aria-label="Toggle AI assistant"
        >
          {isOpen
            ? <X size={22} className="text-white" />
            : <img src="/Gyan_Chand.png" alt="Gyan Chand" className="w-14 h-14 rounded-full object-cover" />
          }
        </button>
      </div>
    </>
  );
};

export default ChatbotWidget;
